// Step row used on the donor / carrier home below the active
// MedicalHistoryStepCard. Renders one of two visual modes but with
// identical layout (icon on the left, content middle, indicator right):
//   - Actionable (`onPress` set) → ChevronRight on the right, full opacity
//   - Read-only (no `onPress`)   → Lock on the right, dimmed
//
// If no `imageSource` is supplied a step-number badge fills the icon
// slot so the layout still reads as a row. The actual 3D icons for
// steps 3-6 should be sourced from thiings.co (or similar) and dropped
// into src/apps/moon/assets/images/you-icons/.
//
// Real product spec for each step (donor agency partnerships, screening
// flow, contract upload, donation telemetry) is tracked in
// docs/moon-launch-plan/EXECUTION_PLAN.md task 1.5 part 5.
import { View, Text, Image, Platform } from "react-native";
import { ChevronRight, Lock } from "lucide-react-native";
import { Row, Tag } from "@louise/moon-ui";
import { EMBER, colors } from "@louise/moon-ui/theme/tokens";
import { withAlpha } from "@louise/theme/tokens";

type Props = {
  stepNumber: number;
  totalSteps: number;
  title: string;
  subtitle: string;
  imageSource?: number;
  onPress?: () => void;
};

export default function HelperJourneyStep({
  stepNumber,
  totalSteps,
  title,
  subtitle,
  imageSource,
  onPress,
}: Props) {
  const isActionable = !!onPress;

  const leftIcon =
    imageSource !== undefined ? (
      <Image
        source={imageSource}
        style={{
          width: 44,
          height: 44,
          ...(Platform.OS === "web" && { mixBlendMode: "overlay" }),
        }}
        resizeMode="contain"
      />
    ) : (
      <View
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          borderWidth: 1.5,
          borderColor: withAlpha(colors.softBrownLight, 0.25),
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 16,
            fontWeight: "700",
            color: EMBER.colors.textLight,
          }}
        >
          {stepNumber}
        </Text>
      </View>
    );

  const rightIndicator = isActionable ? (
    <ChevronRight size={16} color={EMBER.colors.textFaint} strokeWidth={1.5} />
  ) : (
    <Lock size={14} color={EMBER.colors.textFaint} strokeWidth={1.75} />
  );

  const meta = (
    <Tag
      label={`Step ${stepNumber} of ${totalSteps}`}
      style={{ alignSelf: "flex-start" }}
    />
  );

  return (
    <Row
      onPress={onPress}
      title={title}
      subtitle={subtitle}
      meta={meta}
      leading={leftIcon}
      leadingSize={44}
      trailing={rightIndicator}
      chevron={false}
      style={{ marginTop: 12 }}
      paddingHorizontal={18}
      paddingVertical={18}
      muted={!isActionable}
      titleStyle={{
        fontSize: 16,
        fontWeight: "600",
        color: EMBER.colors.text,
      }}
      subtitleStyle={{
        fontSize: 13,
        color: EMBER.colors.textLight,
        lineHeight: 18,
      }}
    />
  );
}
